

export type Record<K extends keyof any = keyof any, V = any> = { [key in K]: V };

export namespace Record 
{
    /**
     * Creates a record from key-value pairs.
     * @param pairs 
     * @returns 
     */
    export const create: <K extends keyof any, V>(pairs: [K, V][]) => Record<K, V> = Object.fromEntries;

    /**
     * Determines if a record contains a certain key.
     * @param record Record holding key-value pairs.
     * @param key Key to look for in the supplied record.
     * @returns A boolean revealing if the key is found in the record.
     */
    export function hasKey<K extends keyof any, V>(record: Record<K, V>, key: K): boolean
    {
        return record.hasOwnProperty(key);
    }

    /**
     * Calls a defined callback function on each element of a record, and returns a new record that contains the results. 
     * @param record The source record on which the callback function is to be called for each key-value pair.
     * @param callbackfn A function that accepts up to two arguments. The map method calls the callbackfn function one time for each element in the record.
     * @returns A new record containing the results.
     */
    export function map<R, P, K extends (keyof R & keyof P)>(record: R, callbackfn: (value: R[K], key: K) => P[K]): P;
    export function map<R, V>(record: R, callbackfn: (value: R[keyof R], key: keyof R) => V): Record<keyof R, V>;
    export function map<R, P, K extends (keyof R & keyof P)>(record: R, callbackfn: (value: R[K], key: K) => P[K]): P
    {
        const keys = Record.keys(record) as K[];
        const result: Partial<P> = {};
        keys.forEach(key => result[key] = callbackfn(record[key], key as K));
        return result as P;
    }

    export const entries: <R>(record: R) => [keyof R, R[keyof R]][] = Object.entries;

    export const keys: <R>(record: R) => (keyof R)[] = Object.keys;

    export const values: <R>(record: R) => R[keyof R][] = Object.values;

    export const from: <K extends keyof any, V>(entries: [K, V][]) => Record<K, V> = Object.fromEntries;

    export function remap<R, K extends keyof R, P>(record: R, callbackfn: (entry: [keyof R, R[keyof R]]) => [K, P]): Record<K, P>
    {
        const entries = Record.entries(record);
        const projection = entries.map(callbackfn);
        const result = Record.from(projection);
        return result;
    }

    export function hasKeys<V>(targetRecord: Record<string, number>, keys: string[]): boolean
    {
        return keys.every(key => Record.hasKey(targetRecord, key));
    }
}
